/**
 * The on-screen way in: a 28px circular button that opens the assistant.
 *
 * WHERE IT SITS, AND WHY NOT A FLOATING BUBBLE
 *
 * Beside the sidebar's ⌘K trigger, in the app's always-present chrome. A
 * floating bubble would need z-index and collision arithmetic against the
 * support widget — which is draggable — and would read as bolted on rather
 * than part of the product. The sidebar has neither problem, and puts this next
 * to the other "ask the app something" control.
 *
 * WHAT IT IS NOT
 *
 * Not a robot, not a face, not an animated character. The assistant answers
 * questions inside an organization's own data and asks before it changes
 * anything; a persona implies more autonomy than the gate model grants.
 * Sparkles is the house AI mark across both products, and teal is the WeaveIn
 * brand token — so it reads as this product's, not as a neutral grey control
 * that could belong to anything.
 *
 * Its own component rather than markup inside the sidebar so the same control
 * can appear wherever chrome exists later (the V1 sidebar, a mobile bar) and
 * carry its label rules with it.
 */
import React from 'react';
import { Sparkles } from 'lucide-react';

export interface CopilotLauncherProps {
  /** Open the assistant. */
  onOpen: () => void;
  /** What this organization calls it. Used verbatim in the label. */
  assistantName: string;
  className?: string;
}

export function CopilotLauncher({ onOpen, assistantName, className = '' }: CopilotLauncherProps) {
  // The label carries the org's own name, not "Copilot". A control whose label
  // disagrees with the panel it opens is worse than a generic one — which is
  // why the name is resolved by the capability probe before this renders,
  // rather than after the first answer comes back.
  const label = `Ask ${assistantName}`;

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      title={label}
      className={
        'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#0D9488] ' +
        'text-white transition-colors hover:bg-[#0F766E] focus:outline-none ' +
        'focus-visible:ring-2 focus-visible:ring-[#0D9488] focus-visible:ring-offset-1 ' +
        className
      }
    >
      <Sparkles className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

export default CopilotLauncher;
